import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CaretDown,
  Coins,
  PaperPlaneTilt,
  User,
  UsersThree,
  WarningCircle,
} from '@phosphor-icons/react';

import { listTeams } from '@/api/teams';
import { listTeamCategories } from '@/api/teamCategories';
import {
  listMemberNoticeLines,
  sendMemberNotice,
  type MemberLineStatus,
  type MemberNoticeLine,
  type Period,
} from '@/api/notices';
import { formatMinor } from './format';
import { MemberFeeOverrideSheet } from './MemberFeeOverrideSheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination, useTablePaginationState } from '@/components/ui/table-pagination';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { RowActions, type RowAction } from '@/components/ui/row-actions';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn } from '@/lib/utils';

// The status tabs, in display order. 'all' is prepended in the UI. Mirrors the
// backend ML* statuses; counts come from the list response.
const STATUS_TABS: MemberLineStatus[] = [
  'paid',
  'noticed',
  'overdue',
  'tryout',
  'to_notice',
  'fee_free',
  'deregistered',
  'cancelled',
];

// Scannable tints per status, following the ReasonCell convention (light tint +
// ring in light mode, translucent fill in dark).
const STATUS_TONE: Record<MemberLineStatus, string> = {
  paid: 'bg-emerald-100 text-emerald-900 ring-emerald-300/70 dark:bg-emerald-500/15 dark:text-emerald-100 dark:ring-emerald-500/40',
  noticed:
    'bg-lime-100 text-lime-950 ring-lime-300/70 dark:bg-lime-500/15 dark:text-lime-100 dark:ring-lime-500/40',
  overdue:
    'bg-red-100 text-red-900 ring-red-300/70 dark:bg-red-500/15 dark:text-red-100 dark:ring-red-500/40',
  tryout:
    'bg-amber-100 text-amber-900 ring-amber-300/70 dark:bg-amber-500/15 dark:text-amber-100 dark:ring-amber-500/40',
  to_notice:
    'bg-sky-100 text-sky-900 ring-sky-300/70 dark:bg-sky-500/15 dark:text-sky-100 dark:ring-sky-500/40',
  fee_free: 'bg-muted text-foreground/70 ring-border',
  deregistered: 'bg-muted text-foreground/70 ring-border',
  cancelled: 'bg-muted text-foreground/60 ring-border',
};

interface Props {
  slug: string;
}

// MembersList is the Notices → Members tab: a member-centric worklist. Where
// the Teams tab answers "which teams have we started?", this answers "find a
// person and see / act on their notice for a period". Filters (period, year,
// category, team, search) + status tabs with counts, and per-row actions to
// resend a notice, edit that member's fees, or jump to their history / profile.
export function MembersList({ slug }: Props) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const locale = i18n.language;
  const currentYear = new Date().getFullYear();

  // Deep-linkable filters live in the URL (namespaced m_* so they don't clash
  // with the page's tab/team params). Search stays local (ephemeral).
  const [sp, setSp] = useSearchParams();
  // Period isn't a member-finder concern, so it's not a visible filter — but
  // fees/statuses are period-scoped in the engine, so we resolve against the
  // current term (spring Jan–Jun, autumn Jul–Dec) by default.
  const period: Period = new Date().getMonth() >= 6 ? 'autumn' : 'spring';
  const year = Number(sp.get('m_year')) || currentYear;
  const statuses = (sp.get('m_status') || '').split(',').filter(Boolean) as MemberLineStatus[];
  const categoryId = sp.get('m_cat') || '';
  const teamId = sp.get('m_team') || '';

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('members_page'); // reset pagination on any filter change
    setSp(next, { replace: true });
  };

  const toggleStatus = (s: MemberLineStatus) => {
    const next = statuses.includes(s) ? statuses.filter((x) => x !== s) : [...statuses, s];
    setParam('m_status', next.join(','));
  };

  // Debounced free-text search over member name.
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const { page, size, setPage, setSize } = useTablePaginationState({
    prefix: 'members',
    defaultSize: 25,
  });
  const offset = (page - 1) * size;

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug, { limit: 200, sort: 'name' }),
    enabled: !!token,
  });
  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token,
  });

  const linesQuery = useQuery({
    queryKey: [
      'notices-member-lines',
      slug,
      period,
      year,
      categoryId,
      teamId,
      statuses.join(','),
      search,
      page,
      size,
    ],
    queryFn: () =>
      listMemberNoticeLines(token, slug, {
        period,
        year,
        statuses,
        limit: size,
        offset,
        ...(categoryId ? { category_id: categoryId } : {}),
        ...(teamId ? { team_id: teamId } : {}),
        ...(search ? { q: search } : {}),
      }),
    enabled: !!token,
  });

  const counts = linesQuery.data?.counts ?? {};
  const rows = linesQuery.data?.rows ?? [];
  const total = linesQuery.data?.total ?? 0;

  // Fee-override sheet target (null = closed).
  const [feeTarget, setFeeTarget] = useState<MemberNoticeLine | null>(null);

  const invalidateLines = () =>
    void qc.invalidateQueries({ queryKey: ['notices-member-lines', slug] });

  const sendM = useMutation({
    mutationFn: (line: MemberNoticeLine) =>
      sendMemberNotice(token, slug, {
        team_id: line.team_id,
        member_id: line.member_id,
        period,
        year,
      }),
    onSuccess: invalidateLines,
  });

  async function onSend(line: MemberNoticeLine) {
    const resend = line.status !== 'to_notice';
    const ok = await confirm({
      title: resend ? t('notices.members.resendTitle') : t('notices.members.sendTitle'),
      body: t('notices.members.sendBody', { name: line.member_name, team: line.team_name }),
      confirmLabel: t('notices.members.sendCta'),
    });
    if (ok) sendM.mutate(line);
  }

  function rowActions(line: MemberNoticeLine): RowAction[] {
    const canSend = line.status === 'to_notice' || line.status === 'overdue' || line.status === 'noticed';
    return [
      {
        id: 'send',
        label: line.status === 'to_notice' ? t('notices.members.send') : t('notices.members.resend'),
        icon: PaperPlaneTilt,
        disabled: !canSend || sendM.isPending,
        onSelect: () => void onSend(line),
      },
      {
        id: 'fees',
        label: t('notices.members.editFees'),
        icon: Coins,
        onSelect: () => setFeeTarget(line),
      },
      {
        id: 'member',
        label: t('notices.members.openMember'),
        icon: User,
        // The member drawer already carries a per-member Logs tab (audit rows
        // tagged with this member as subject), so this doubles as "view history".
        onSelect: () => navigate(`/orgs/${slug}/members?member=${line.member_id}`),
      },
    ];
  }

  const teams = teamsQuery.data?.teams ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const yearOptions = useMemo(
    () => [currentYear + 1, currentYear, currentYear - 1, currentYear - 2],
    [currentYear],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <section className="flex flex-col gap-3 rounded-xl border border-border bg-background p-4 md:p-5">
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.members.col.status')}</span>
            <StatusMultiSelect
              selected={statuses}
              counts={counts}
              onToggle={toggleStatus}
              onClear={() => setParam('m_status', '')}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.run.year')}</span>
            <Select value={String(year)} onValueChange={(v) => setParam('m_year', v)}>
              <SelectTrigger className="min-h-[var(--tap)] min-w-[7rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.members.category')}</span>
            <Select value={categoryId || 'all'} onValueChange={(v) => setParam('m_cat', v === 'all' ? '' : v)}>
              <SelectTrigger className="min-h-[var(--tap)] min-w-[10rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('notices.members.allCategories')}</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.run.team')}</span>
            <Select value={teamId || 'all'} onValueChange={(v) => setParam('m_team', v === 'all' ? '' : v)}>
              <SelectTrigger className="min-h-[var(--tap)] min-w-[10rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('notices.members.allTeams')}</SelectItem>
                {teams.map((tm) => (
                  <SelectItem key={tm.id} value={tm.id}>
                    {tm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="flex flex-1 flex-col gap-1 text-sm">
            <span className="font-medium">{t('notices.members.search')}</span>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('notices.members.searchPlaceholder')}
              className="min-h-[var(--tap)] min-w-[12rem] rounded-pill border border-border bg-background px-4"
            />
          </label>
        </div>
      </section>

      <MembersTable
        rows={rows}
        total={total}
        loading={linesQuery.isLoading}
        locale={locale}
        page={page}
        size={size}
        setPage={setPage}
        setSize={setSize}
        rowActions={rowActions}
        onOpenProfile={(id) => navigate(`/orgs/${slug}/members?member=${id}`)}
      />

      {feeTarget && (
        <MemberFeeOverrideSheet
          slug={slug}
          member={{ id: feeTarget.member_id, name: feeTarget.member_name }}
          teamId={feeTarget.team_id}
          teamName={feeTarget.team_name}
          period={period}
          onClose={() => setFeeTarget(null)}
          onSaved={() => {
            setFeeTarget(null);
            invalidateLines();
          }}
        />
      )}
    </div>
  );
}

function MembersTable({
  rows,
  total,
  loading,
  locale,
  page,
  size,
  setPage,
  setSize,
  rowActions,
  onOpenProfile,
}: {
  rows: MemberNoticeLine[];
  total: number;
  loading: boolean;
  locale: string;
  page: number;
  size: number;
  setPage: (p: number) => void;
  setSize: (s: number) => void;
  rowActions: (line: MemberNoticeLine) => RowAction[];
  onOpenProfile: (memberID: string) => void;
}) {
  const { t } = useTranslation('common');

  const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    try {
      return new Intl.DateTimeFormat(locale || 'sv-SE', { dateStyle: 'medium' }).format(
        new Date(iso),
      );
    } catch {
      return '—';
    }
  };

  if (loading) {
    return <TableSkeleton columns={6} />;
  }
  if (rows.length === 0) {
    return <EmptyState icon={<UsersThree />} title={t('notices.members.empty')} />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table className="min-w-[48rem]">
          <TableHeader>
            <TableRow>
              <TableHead>{t('notices.members.col.member')}</TableHead>
              <TableHead>{t('notices.members.col.team')}</TableHead>
              <TableHead className="text-right">{t('notices.members.col.amount')}</TableHead>
              <TableHead>{t('notices.members.col.noticeDate')}</TableHead>
              <TableHead>{t('notices.members.col.dueDate')}</TableHead>
              <TableHead>{t('notices.members.col.status')}</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((line) => (
              <TableRow key={`${line.member_id}:${line.team_id}`} className="group">
                <TableCell className="font-medium">
                  <button
                    type="button"
                    onClick={() => onOpenProfile(line.member_id)}
                    className="flex items-center gap-1.5 rounded-pill text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {line.member_name}
                    {line.missing_contact && (
                      <WarningCircle
                        size={15}
                        weight="fill"
                        className="text-red-500"
                        aria-label={t('notices.table.unreachable')}
                      />
                    )}
                  </button>
                </TableCell>
                <TableCell className="text-foreground/70">{line.team_name}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.amount_minor > 0
                    ? formatMinor(line.amount_minor, line.currency ?? 'SEK', locale)
                    : '—'}
                </TableCell>
                <TableCell className="text-foreground/70">{fmtDate(line.notice_date)}</TableCell>
                <TableCell className="text-foreground/70">{fmtDate(line.due_date)}</TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'inline-flex items-center rounded-pill px-2.5 py-0.5 text-xs font-semibold ring-1',
                      STATUS_TONE[line.status],
                    )}
                  >
                    {t(`notices.members.status.${line.status}`)}
                  </span>
                </TableCell>
                <TableCell className="w-16">
                  <RowActions menuActions={rowActions(line)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        total={total}
        page={page}
        size={size}
        onPageChange={setPage}
        onSizeChange={setSize}
      />
    </div>
  );
}

// StatusMultiSelect is a compact dropdown for filtering by one or more statuses,
// with each option's live count. Empty selection = all. Replaces the old row of
// per-status pill tabs to save horizontal space.
function StatusMultiSelect({
  selected,
  counts,
  onToggle,
  onClear,
}: {
  selected: MemberLineStatus[];
  counts: Record<string, number>;
  onToggle: (s: MemberLineStatus) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const triggerLabel =
    selected.length === 0
      ? t('notices.members.allStatuses')
      : t('notices.members.statusesSelected', { count: selected.length });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-w-[12rem] items-center justify-between gap-2 rounded-pill border px-4 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selected.length > 0
              ? 'border-foreground bg-foreground text-background'
              : 'border-border text-foreground/70 hover:bg-muted',
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <CaretDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', open && 'rotate-180')} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[16rem] p-0" side="bottom" sideOffset={4}>
        <div className="max-h-64 overflow-y-auto py-1">
          {STATUS_TABS.map((s) => (
            <label
              key={s}
              className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-muted/50"
            >
              <Checkbox checked={selected.includes(s)} onChange={() => onToggle(s)} />
              <span className="flex-1 truncate text-sm text-foreground">
                {t(`notices.members.status.${s}`)}
              </span>
              <span className="tabular-nums text-xs text-foreground/45">{counts[s] ?? 0}</span>
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="border-t border-border px-3 py-2">
            <button
              type="button"
              onClick={onClear}
              className="font-heading text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none"
            >
              {t('notices.members.status.all')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
